import React, { useState, useRef, useEffect, useMemo } from "react";
import { IoChevronDown } from "react-icons/io5";

const PortfolioSelector = ({
    portfolioFacilityNames = [],
    value,
    onChange,
    size,
    isPortfolioLoading,
    isDisabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);

    // Check if the selector should be disabled
    const effectivelyDisabled = isDisabled || isPortfolioLoading;
    const [activeIndex, setActiveIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const optionsRef = useRef([]);
    const [displayValue, setDisplayValue] = useState("Select Portfolio Facility");

    // Prepare flattened options list for keyboard navigation
    const flattenedOptions = useMemo(() => {
        // Safely wrap facility names to ensure it's always an array
        const safeFacilityNames = Array.isArray(portfolioFacilityNames) ? portfolioFacilityNames : [];

        return [
            { id: "placeholder", value: "", label: "Select Portfolio Facility" },
            ...safeFacilityNames.map(facilityName => ({
                id: facilityName,
                value: facilityName,
                label: facilityName
            }))
        ];
    }, [portfolioFacilityNames]);

    // Update display value when value changes
    useEffect(() => {
        if (!value || value === "") {
            setDisplayValue("Select Portfolio Facility");
            return;
        }
        setDisplayValue(value);
    }, [value]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update refs array when options change
    useEffect(() => {
        optionsRef.current = optionsRef.current.slice(0, flattenedOptions.length);
    }, [flattenedOptions]);

    // Keyboard event handler
    const handleKeyDown = (e) => {
        if (effectivelyDisabled) return;

        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                // Start at selected value if available, otherwise at placeholder
                const selectedIndex = value
                    ? flattenedOptions.findIndex(opt => opt.value === value)
                    : 0;
                setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
            }
            return;
        }

        const totalOptions = flattenedOptions.length;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex < totalOptions - 1 ? prevIndex + 1 : 0
                );
                break;

            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex > 0 ? prevIndex - 1 : totalOptions - 1
                );
                break;

            case 'Enter':
            case ' ':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < totalOptions) {
                    const selectedOption = flattenedOptions[activeIndex];
                    handleSelect(selectedOption.value);
                }
                break;

            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;

            case 'Tab':
                setIsOpen(false);
                break;

            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;

            case 'End':
                e.preventDefault();
                setActiveIndex(totalOptions - 1);
                break;

            default:
                const key = e.key.toLowerCase();
                if (key.length === 1 && /[a-z0-9]/.test(key)) {
                    const nextIndex = flattenedOptions.findIndex((option, index) =>
                        index > activeIndex && option.label.toLowerCase().startsWith(key)
                    );

                    if (nextIndex !== -1) {
                        setActiveIndex(nextIndex);
                    } else {
                        const firstIndex = flattenedOptions.findIndex((option) =>
                            option.label.toLowerCase().startsWith(key)
                        );

                        if (firstIndex !== -1) {
                            setActiveIndex(firstIndex);
                        }
                    }
                }
                break;
        }
    };

    // Scroll active option into view
    useEffect(() => {
        if (isOpen && activeIndex >= 0 && optionsRef.current[activeIndex]) {
            optionsRef.current[activeIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [activeIndex, isOpen]);

    const handleSelect = (selectedValue) => {
        if (effectivelyDisabled) {
            return;
        }
        setIsOpen(false);
        if (onChange) {
            onChange(selectedValue);
        } else {
            console.warn("   ⚠️ No onChange handler provided!");
        }
    };

    return (
        <div
            className="relative w-full sm:w-[150px] md:w-[150px] lg:min-w-[200px] xl:min-w-[250px]"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={effectivelyDisabled ? "-1" : "0"}
        >
            <div
                className={`flex items-center justify-between px-3 py-2 bg-white border-b border-[#89949f] ${effectivelyDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } ${size === "small" ? "text-sm" : "text-base"} ${isOpen ? "border-blue-500" : ""
                    }`}
                onClick={() => {
                    if (!effectivelyDisabled) {
                        setIsOpen(!isOpen);
                        // When opening, set active index to current value
                        if (!isOpen) {
                            const selectedIndex = value
                                ? flattenedOptions.findIndex(opt => opt.value === value)
                                : 0;
                            setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
                        }
                    }
                }}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls="portfolio-selector"
                role="combobox"
                aria-disabled={effectivelyDisabled}
            >
                <span className="truncate overflow-hidden">
                    {isPortfolioLoading ? "Loading..." : displayValue}
                </span>
                {!effectivelyDisabled && (
                    <IoChevronDown
                        className={`w-[12px] h-[12px] transition-transform ml-2 ${isOpen ? 'rotate-180' : ''
                            }`}
                    />
                )}
            </div>

            {isOpen && !isPortfolioLoading && (
                <div
                    className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    style={{ minWidth: '200px' }}
                    role="listbox"
                    id="portfolio-selector"
                >
                    <ul className="py-1">
                        {flattenedOptions.map((option, index) => (
                            <li
                                key={option.id}
                                ref={el => optionsRef.current[index] = el}
                                className={`px-4 py-2 cursor-pointer font-medium ${option.value === "" ? "text-gray-500" : "text-gray-900"
                                    } ${activeIndex === index ? "bg-blue-100" : "hover:bg-gray-100"
                                    }`}
                                onClick={() => handleSelect(option.value)}
                                onMouseEnter={() => setActiveIndex(index)}
                                role="option"
                                aria-selected={activeIndex === index}
                                tabIndex="-1"
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default PortfolioSelector; 